/**
 * Catalog of Quote in-module views (history / new) plus outbound day-of-work
 * links. Day queues live on Inicio. Operations and commissions stay top-level
 * nav: other roles use them. The desk only points at them.
 */
import type { LucideIcon } from 'lucide-react';
import {
  History,
  Package,
  Percent,
  PlusCircle,
} from 'lucide-react';
import { ROUTES } from '../routes';
import { OPERATIONS_MODULE_ENABLED } from './featureFlags';

export const QUOTE_VIEW_IDS = ['history', 'form'] as const;
export type QuoteViewId = (typeof QUOTE_VIEW_IDS)[number];
export type { QuoteDeskBucket } from './quoteDeskLogic';
export { classifyQuoteForDesk, quoteDeskCounts } from './quoteDeskLogic';

export interface QuoteNavItemDef {
  view: QuoteViewId;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

export interface QuoteNavGroupDef {
  id: string;
  groupLabelKey: string;
  items: QuoteNavItemDef[];
}

export interface QuoteDayLinkDef {
  to: string;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  routeKey: 'operations' | 'commissions';
}

export const QUOTE_NAV_GROUPS: QuoteNavGroupDef[] = [
  {
    id: 'lookup',
    groupLabelKey: 'quote.nav.groups.lookup',
    items: [
      {
        view: 'history',
        icon: History,
        labelKey: 'quote.nav.items.history.label',
        descriptionKey: 'quote.nav.items.history.description',
      },
      {
        view: 'form',
        icon: PlusCircle,
        labelKey: 'quote.nav.items.form.label',
        descriptionKey: 'quote.nav.items.form.description',
      },
    ],
  },
];

export const QUOTE_DAY_LINKS: QuoteDayLinkDef[] = [
  ...(OPERATIONS_MODULE_ENABLED
    ? [{
        to: `${ROUTES.operations}?view=form`,
        icon: Package,
        routeKey: 'operations' as const,
        labelKey: 'quote.nav.items.operations.label',
        descriptionKey: 'quote.nav.items.operations.description',
      }]
    : []),
  {
    to: `${ROUTES.commissions}?status=pending_commission`,
    icon: Percent,
    routeKey: 'commissions',
    labelKey: 'quote.nav.items.commissions.label',
    descriptionKey: 'quote.nav.items.commissions.description',
  },
];

export function quotePath(view: QuoteViewId): string {
  return `${ROUTES.quote}?view=${view}`;
}

export function quoteItemFor(view: string): QuoteNavItemDef | undefined {
  return QUOTE_NAV_GROUPS[0]?.items.find((item) => item.view === view);
}

export function quoteSidebarSubItems() {
  return QUOTE_NAV_GROUPS.flatMap((group) =>
    group.items.map((navItem, index) => ({
      id: quotePath(navItem.view),
      labelKey: navItem.labelKey,
      icon: navItem.icon,
      descriptionKey: navItem.descriptionKey,
      ...(index === 0 ? { groupLabelKey: group.groupLabelKey } : {}),
    })),
  );
}

